// Coaching memory helpers: migration, expiry, compaction triggers, check-in detection.
//
// Data shape:
//   coachingMemory.noteEntries = [{
//     id: string,
//     text: string,
//     kind: 'durable' | 'transient',
//     createdAt: ISO string,
//     lastConfirmedAt?: ISO string | null,
//     deferredUntil?: ISO string | null,
//   }]
// Legacy `notes` (string, newline-joined) is migrated into noteEntries on load.

import { sanitizeRaces, raceKey } from './racesLifecycle';

const DAY_MS = 24 * 60 * 60 * 1000;
export const TRANSIENT_EXPIRY_DAYS = 21;
export const CHECKIN_MIN_AGE_DAYS = 7;
export const COMPACT_TRIGGER_ENTRIES = 15;
export const COMPACT_TRIGGER_CHARS = 1500;
export const COMPACTED_MAX_ENTRIES = 10;
// Tombstones older than this are garbage-collected. 180 days comfortably
// outlives any stale device (Strava sessions expire far sooner) while keeping
// the deletedNotes array bounded.
export const TOMBSTONE_TTL_DAYS = 180;
// Jaccard token-set similarity above which two notes are treated as the same
// concept for deduplication / deletion. Deliberately high so distinct facts are
// never merged; punctuation/whitespace/dash variants are already collapsed by
// `dedupeKey` before similarity is even considered.
export const DEDUPE_JACCARD_THRESHOLD = 0.8;

const newId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const norm = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');

// Exact tombstone/dedup key: whitespace-collapsed lowercase. Used where we need
// an exact-text identity (e.g. carrying metadata across a compaction).
export const noteKey = (s) => norm(s);

// Stable identity key for an injury structured fact — normalized description.
// Mirrors the server's injury union key (normText of description).
export const injuryKey = (inj) => norm(inj && inj.description);

// Strong dedup key: lowercase, unify all dash variants, strip punctuation, then
// collapse whitespace. This collapses the most common near-duplicate variants
// the extractor produces across sessions — trailing periods, parentheses, and
// en-dash vs hyphen (e.g. "(Apr 20–May 20)." ≡ "Apr 20-May 20"). Two notes with
// the same dedupeKey are considered identical.
export const dedupeKey = (s) => String(s || '')
  .toLowerCase()
  .replace(/[\u2010-\u2015\u2212]/g, '-') // dash variants → hyphen
  .replace(/[^a-z0-9\s-]/g, ' ')          // drop other punctuation
  .replace(/\s+/g, ' ')
  .trim();

const STOPWORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'to', 'of',
  'in', 'on', 'for', 'with', 'and', 'or', 'but', 'will', 'have', 'has', 'had',
  'during', 'this', 'that', 'at', 'by', 'from', 'as', 'it', 'its', 'their',
  'athlete', 'wants', 'want',
]);

// Tokenize a note into a set of meaningful lowercase tokens for similarity.
const tokenize = (s) => {
  const out = new Set();
  for (const t of dedupeKey(s).split(/[\s-]+/)) {
    if (t && !STOPWORDS.has(t)) out.add(t);
  }
  return out;
};

const jaccard = (a, b) => {
  if (!a.size && !b.size) return 1;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
};

// True when two note texts describe the same concept: identical after strong
// normalization, or high token-set overlap. The min-token guard prevents tiny
// notes ("swim", "bike") from collapsing on a single shared word.
export function sameConcept(a, b) {
  if (dedupeKey(a) === dedupeKey(b)) return true;
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (Math.min(ta.size, tb.size) < 3) return false;
  return jaccard(ta, tb) >= DEDUPE_JACCARD_THRESHOLD;
}

// Most recent time a note was created/confirmed, as ms (0 when unknown).
const refTimeMs = (e) => {
  const t = new Date(e && (e.lastConfirmedAt || e.createdAt)).getTime();
  return Number.isFinite(t) ? t : 0;
};

// Time-bound cue words. A single-dated note is treated as expirable ONLY when it
// also carries one of these cues, so durable biographical facts that merely
// mention a year ("started running in 2015") are never auto-expired.
const TIME_BOUND_CUE = /\b(trip|travel(?:ing|ling)?|vacation|holiday|away|abroad|visiting|camp|race|event|marathon|ironman|70\.3|taper|until|through|this week|next week|over the next|for the next|limited|no access|out of town|recovering|deload)\b/i;

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// Detect a specific calendar date embedded in a note's text and return it as a
// timestamp (ms), or null if the note has no fixed date. Handles ISO
// (YYYY-MM-DD) and common "Month D[, YYYY]" / "D Month" prose forms.
//
// Why this matters: the extractor occasionally records a one-off dated event as
// a transient note (e.g. "Did strength work on 2026-06-02"). Such a note is a
// historical fact, not an ongoing state, so a "still true?" check-in is
// nonsensical, and it should quietly expire once the date has passed rather than
// lingering. Callers use this to skip check-ins on dated notes and to expire
// them based on the date in the text (not the note's createdAt).
export function extractNoteDate(text, now = Date.now()) {
  if (!text) return null;
  const s = String(text);
  const referenceYear = new Date(now).getUTCFullYear();

  const iso = s.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) {
    const t = Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isFinite(t) ? t : null;
  }

  const monthName = '(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*';
  // "June 2" / "June 2, 2026" / "Jun 2 2026"
  let m = s.toLowerCase().match(new RegExp(`\\b${monthName}\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`));
  if (m) {
    const year = m[3] ? Number(m[3]) : referenceYear;
    const t = Date.UTC(year, MONTHS[m[1].slice(0, 3)], Number(m[2]));
    return Number.isFinite(t) ? t : null;
  }
  // "2 June" / "2nd June 2026"
  m = s.toLowerCase().match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+${monthName}(?:,?\\s+(\\d{4}))?\\b`));
  if (m) {
    const year = m[3] ? Number(m[3]) : referenceYear;
    const t = Date.UTC(year, MONTHS[m[2].slice(0, 3)], Number(m[1]));
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

// True when the note describes a specific calendar date that has already passed.
export function describesPastDatedEvent(text, now = Date.now()) {
  const t = extractNoteDate(text, now);
  return t !== null && t < now;
}

// Extract every calendar date referenced in a note, as ms timestamps (UTC
// midnight). Handles multiple ISO dates and the "Month D" / "D Month" prose
// forms — enough to recover both ends of a date range like "Apr 20–May 20".
export function extractAllNoteDates(text, now = Date.now()) {
  if (!text) return [];
  const s = String(text);
  const referenceYear = new Date(now).getUTCFullYear();
  const dates = [];
  const push = (t) => { if (Number.isFinite(t)) dates.push(t); };

  const isoRe = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
  let m;
  while ((m = isoRe.exec(s)) !== null) {
    push(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  }

  const monthName = '(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*';
  const mdRe = new RegExp(`\\b${monthName}\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`, 'gi');
  while ((m = mdRe.exec(s)) !== null) {
    const year = m[3] ? Number(m[3]) : referenceYear;
    push(Date.UTC(year, MONTHS[m[1].slice(0, 3).toLowerCase()], Number(m[2])));
  }
  const dmRe = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+${monthName}(?:,?\\s+(\\d{4}))?\\b`, 'gi');
  while ((m = dmRe.exec(s)) !== null) {
    const year = m[3] ? Number(m[3]) : referenceYear;
    push(Date.UTC(year, MONTHS[m[2].slice(0, 3).toLowerCase()], Number(m[1])));
  }
  return dates;
}

// The timestamp after which a note becomes stale, or null if it is not
// time-bound. A note is time-bound when it references a DATE RANGE (two or more
// dates → use the latest, ranges are inherently bounded) OR a single date paired
// with a time-bound cue word (trip / race / "this week" / "until" …). Undated
// notes and single-dated notes without a cue (biographical facts) return null,
// so durable facts are preserved.
export function noteExpiryDate(text, now = Date.now()) {
  const dates = extractAllNoteDates(text, now);
  if (dates.length === 0) return null;
  if (dates.length >= 2) {
    // Range → expires at end of the last referenced day.
    return Math.max(...dates) + DAY_MS;
  }
  if (TIME_BOUND_CUE.test(String(text))) {
    return dates[0] + DAY_MS;
  }
  return null;
}

// True when a note is time-bound and its window has already closed.
export function isExpiredDatedNote(text, now = Date.now()) {
  const exp = noteExpiryDate(text, now);
  return exp !== null && exp < now;
}

// Convert raw stored memory into the current shape. Idempotent.
export function migrateMemory(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const memory = { ...raw };
  if (!Array.isArray(memory.noteEntries)) memory.noteEntries = [];
  if (!Array.isArray(memory.deletedNotes)) memory.deletedNotes = [];
  if (!Array.isArray(memory.deletedFacts)) memory.deletedFacts = [];

  // Migrate legacy `notes` string (newline-joined facts) into individual durable entries.
  if (typeof memory.notes === 'string' && memory.notes.trim()) {
    const baseTime = memory.lastUpdated || new Date().toISOString();
    const existingTexts = new Set(memory.noteEntries.map(e => norm(e.text)));
    const lines = memory.notes
      .split(/\n+/)
      .map(l => l.trim())
      .filter(Boolean);
    for (const line of lines) {
      if (existingTexts.has(norm(line))) continue;
      memory.noteEntries.push({
        id: newId(),
        text: line,
        // We don't know the original kind, so default to durable to avoid mass-deletion
        // on first load. Future entries will be tagged correctly by the extractor.
        kind: 'durable',
        createdAt: baseTime,
        lastConfirmedAt: null,
        deferredUntil: null,
      });
      existingTexts.add(norm(line));
    }
    memory.notes = '';
  }

  // Ensure each entry has required fields.
  memory.noteEntries = memory.noteEntries.map(e => ({
    id: e.id || newId(),
    text: e.text || '',
    kind: e.kind === 'durable' ? 'durable' : 'transient',
    createdAt: e.createdAt || new Date().toISOString(),
    lastConfirmedAt: e.lastConfirmedAt || null,
    deferredUntil: e.deferredUntil || null,
  })).filter(e => e.text);

  // Normalize tombstones: each needs a stable dedup key + a deletedAt.
  memory.deletedNotes = memory.deletedNotes
    .filter(t => t && (t.key || t.text))
    .map(t => ({
      key: t.key || dedupeKey(t.text),
      text: t.text || '',
      deletedAt: t.deletedAt || new Date().toISOString(),
    }));

  // Normalize structured-fact tombstones (race / injury deletions).
  memory.deletedFacts = memory.deletedFacts
    .filter(t => t && t.kind && t.key)
    .map(t => ({
      kind: t.kind,
      key: String(t.key),
      deletedAt: t.deletedAt || new Date().toISOString(),
    }));

  // Apply race lifecycle sanitization: fill missing status, flip upcoming→
  // completed when the date has passed, archive races >30 days past into
  // pastRaces, dedup across both arrays. Idempotent — safe to run on every
  // load. See src/utils/racesLifecycle.js for the full contract.
  const { races, pastRaces } = sanitizeRaces(memory);
  memory.races = races;
  memory.pastRaces = pastRaces;

  return memory;
}

// Drop transient entries that have aged out.
export function expireTransientNotes(memory, now = Date.now()) {
  if (!memory?.noteEntries?.length) return memory;
  const cutoff = now - TRANSIENT_EXPIRY_DAYS * DAY_MS;
  const next = memory.noteEntries.filter(e => {
    if (e.kind !== 'transient') return true;
    // A transient note that names a specific past calendar date is a historical
    // one-off event, not an ongoing state. Expire it once that date has passed
    // (it should never have been stored as a note, but self-heal old data).
    if (describesPastDatedEvent(e.text, now)) return false;
    const ref = new Date(e.lastConfirmedAt || e.createdAt).getTime();
    return Number.isFinite(ref) ? ref > cutoff : true;
  });
  if (next.length === memory.noteEntries.length) return memory;
  return { ...memory, noteEntries: next };
}

// Merge incoming note entries (from AI extraction) into existing entries.
// Dedupes by normalised text; preserves earliest createdAt.
export function mergeNoteEntries(existing = [], incoming = [], now = new Date()) {
  const byKey = new Map(existing.map(e => [norm(e.text), e]));
  for (const inc of incoming) {
    if (!inc?.text) continue;
    const key = norm(inc.text);
    if (byKey.has(key)) {
      // Refresh lastConfirmedAt — the AI just heard it again.
      const cur = byKey.get(key);
      byKey.set(key, { ...cur, lastConfirmedAt: now.toISOString() });
    } else {
      byKey.set(key, {
        id: newId(),
        text: inc.text.trim(),
        kind: inc.kind === 'durable' ? 'durable' : 'transient',
        createdAt: now.toISOString(),
        lastConfirmedAt: null,
        deferredUntil: null,
      });
    }
  }
  return Array.from(byKey.values());
}

// ── Tombstones (durable deletion across union merges) ────────────────────────
//
// The server and client both UNION note entries so a stale device can never
// wipe notes another device added. The flip side is that a plain removal is
// resurrected by the next union. Tombstones fix this: deleting a note records a
// `{ key, text, deletedAt }` marker that travels with the memory blob and
// suppresses that note (and its near-duplicates) on every merge — UNLESS the
// note was created/confirmed AFTER the tombstone (the fact was genuinely
// re-learned). Tombstones are unioned by key (newest deletedAt wins) and GC'd
// after TOMBSTONE_TTL_DAYS.

export function makeTombstone(text, deletedAt = new Date().toISOString()) {
  return { key: dedupeKey(text), text: String(text || ''), deletedAt };
}

export function gcTombstones(tombstones = [], now = Date.now()) {
  const cutoff = now - TOMBSTONE_TTL_DAYS * DAY_MS;
  return tombstones.filter(t => {
    const ts = new Date(t.deletedAt).getTime();
    return Number.isFinite(ts) ? ts > cutoff : false;
  });
}

// Union two tombstone lists by key, keeping the most recent deletedAt, then GC.
export function mergeTombstones(a = [], b = [], now = Date.now()) {
  const byKey = new Map();
  for (const t of [...a, ...b]) {
    if (!t || (!t.key && !t.text)) continue;
    const key = t.key || dedupeKey(t.text);
    const cur = byKey.get(key);
    const deletedAt = t.deletedAt || new Date().toISOString();
    if (!cur || new Date(deletedAt) > new Date(cur.deletedAt)) {
      byKey.set(key, { key, text: t.text || cur?.text || '', deletedAt });
    }
  }
  return gcTombstones(Array.from(byKey.values()), now);
}

// Add a tombstone for a note to a memory-style deletedNotes list.
export function addTombstone(deletedNotes = [], text, deletedAt = new Date().toISOString()) {
  return mergeTombstones(deletedNotes, [makeTombstone(text, deletedAt)], new Date(deletedAt).getTime());
}

// Drop entries suppressed by a tombstone. An entry is suppressed when a
// tombstone matches its concept (exact dedupeKey or high token similarity) AND
// the entry was last touched strictly before the tombstone was created.
// A note learned at or after deletion (refTime >= deletedAt) survives.
export function applyTombstones(entries = [], tombstones = []) {
  if (!tombstones.length) return entries;
  return entries.filter(e => {
    const ref = refTimeMs(e);
    for (const t of tombstones) {
      const del = new Date(t.deletedAt).getTime();
      if (!Number.isFinite(del)) continue;
      if (ref < del && sameConcept(e.text, t.text || t.key)) return false;
    }
    return true;
  });
}

// ── Structured-fact tombstones (durable deletion of races / injuries) ────────
//
// noteEntries had tombstones since PR #11, but the server also UNIONS the
// `races`, `pastRaces`, and `injuries` arrays, so deleting one of those locally
// was resurrected by the next save-echo or poll. A generalized tombstone
// channel — `deletedFacts[] = { kind: 'race'|'injury', key, deletedAt }` —
// fixes this the same way notes are fixed: a deletion records a marker that
// travels with the memory blob and suppresses the fact on every merge, on
// every array (races AND pastRaces), UNLESS the fact was (re-)learned AFTER the
// deletion. "Re-learned" is proven by an effective `memoryUpdatedAt` timestamp
// the extractor stamps on newly-learned structured facts; an old/stale copy
// lacking that stamp has an effective time of 0 and stays suppressed. Keys are
// the same stable identities the merges already use (`raceKey`, `injuryKey`).

// Effective timestamp of a structured fact (ms). Genuinely (re-)learned facts
// carry `memoryUpdatedAt`; legacy/stale copies have none → 0, so a tombstone
// suppresses them while a post-deletion re-learn survives.
const factRefTimeMs = (f) => {
  const t = new Date(f && f.memoryUpdatedAt).getTime();
  return Number.isFinite(t) ? t : 0;
};

// Compose the composite id for a fact tombstone (kind + stable key).
const factTombId = (t) => `${t.kind}|${t.key}`;

export function makeFactTombstone(kind, key, deletedAt = new Date().toISOString()) {
  return { kind, key: String(key || ''), deletedAt };
}

export function gcFactTombstones(tombstones = [], now = Date.now()) {
  const cutoff = now - TOMBSTONE_TTL_DAYS * DAY_MS;
  return (Array.isArray(tombstones) ? tombstones : []).filter(t => {
    if (!t || !t.kind || !t.key) return false;
    const ts = new Date(t.deletedAt).getTime();
    return Number.isFinite(ts) ? ts > cutoff : false;
  });
}

// Union two fact-tombstone lists by (kind,key), keeping the most recent
// deletedAt, then GC.
export function mergeFactTombstones(a = [], b = [], now = Date.now()) {
  const byId = new Map();
  for (const t of [...(a || []), ...(b || [])]) {
    if (!t || !t.kind || !t.key) continue;
    const id = factTombId(t);
    const deletedAt = t.deletedAt || new Date().toISOString();
    const cur = byId.get(id);
    if (!cur || new Date(deletedAt) > new Date(cur.deletedAt)) {
      byId.set(id, { kind: t.kind, key: t.key, deletedAt });
    }
  }
  return gcFactTombstones(Array.from(byId.values()), now);
}

// Add a fact tombstone to a memory-style deletedFacts list.
export function addFactTombstone(deletedFacts = [], kind, key, deletedAt = new Date().toISOString()) {
  return mergeFactTombstones(deletedFacts, [makeFactTombstone(kind, key, deletedAt)], new Date(deletedAt).getTime());
}

// Merge freshly-extracted structured facts (races/injuries) into an existing
// list, used by App.js's `mergeMemory` for both `races` and `injuries`.
//
// The gap this closes: the previous inline logic in `mergeMemory` only ever
// APPENDED updates whose key was absent locally, and stamped `memoryUpdatedAt`
// only on those brand-new entries. An update whose key matched an existing
// entry was silently dropped (already "known", nothing to add) — so a
// stale-device re-mention of a fact the athlete had deleted elsewhere never
// got a fresh `memoryUpdatedAt`. On sync, the server compared the (still
// pre-deletion) timestamp against the tombstone's `deletedAt` and suppressed
// the "re-learned" fact right back out. See applyFactTombstones above and the
// server's mirrored `applyFactTombstonesToArray` in api/coachingData/index.js.
//
// Fix: every update whose key matches an existing entry now merges its fields
// into that entry AND re-stamps `memoryUpdatedAt = nowIso` — proving (to a
// tombstone check on either side) that the fact was genuinely re-learned just
// now. Existing entries with NO matching update are carried through
// completely untouched (no restamping) so an unrelated fact's timestamp never
// drifts just because a sibling fact was re-mentioned in the same turn.
// Genuinely new keys are appended, stamped the same way as before.
export function mergeStructuredFactUpdates(existingList = [], updates = [], keyFn, nowIso = new Date().toISOString()) {
  const list = Array.isArray(existingList) ? existingList : [];
  const upd = Array.isArray(updates) ? updates : [];
  if (upd.length === 0) return list;

  const updatesByKey = new Map();
  for (const u of upd) {
    if (!u || typeof u !== 'object') continue;
    updatesByKey.set(keyFn(u), u);
  }

  const matchedKeys = new Set();
  const merged = list.map(item => {
    const key = keyFn(item);
    const u = updatesByKey.get(key);
    if (!u) return item; // untouched — carry through, no restamp
    matchedKeys.add(key);
    return { ...item, ...u, memoryUpdatedAt: nowIso };
  });

  for (const [key, u] of updatesByKey) {
    if (!matchedKeys.has(key)) merged.push({ ...u, memoryUpdatedAt: nowIso });
  }
  return merged;
}

// Suppress races/pastRaces/injuries deleted via a fact tombstone. A fact is
// suppressed when a tombstone of its kind matches its stable key AND the fact
// was not (re-)learned at/after the deletion (memoryUpdatedAt >= deletedAt).
// Applied to BOTH race arrays so archive-location can't route around a deletion.
export function applyFactTombstones(memory, tombstones = []) {
  if (!memory || typeof memory !== 'object') return memory;
  const list = Array.isArray(tombstones) ? tombstones : [];
  if (!list.length) return memory;
  const latestByKind = { race: new Map(), injury: new Map() };
  for (const t of list) {
    if (!t || !t.kind || !t.key) continue;
    const m = latestByKind[t.kind];
    if (!m) continue;
    const del = new Date(t.deletedAt).getTime();
    if (!Number.isFinite(del)) continue;
    const cur = m.get(t.key);
    if (cur === undefined || del > cur) m.set(t.key, del);
  }
  const suppressed = (kind, key, ref) => {
    const del = latestByKind[kind].get(key);
    return del !== undefined && ref < del;
  };
  const filterRaces = (arr) => (Array.isArray(arr) ? arr : []).filter(
    r => !suppressed('race', raceKey(r), factRefTimeMs(r))
  );
  const filterInjuries = (arr) => (Array.isArray(arr) ? arr : []).filter(
    i => !suppressed('injury', injuryKey(i), factRefTimeMs(i))
  );
  return {
    ...memory,
    races: filterRaces(memory.races),
    pastRaces: filterRaces(memory.pastRaces),
    injuries: filterInjuries(memory.injuries),
  };
}

// Mint tombstones for EVERY currently-known note, race, pastRace, and injury in
// `memory`, merged onto the existing tombstone lists. Returns
// `{ deletedNotes, deletedFacts }`. This is what makes a "Clear memory" /
// "Delete all" durable: the wipe travels as tombstones so the server union and
// any stale device honour it, instead of a null-lastUpdated blank that a later
// sync silently repopulates.
export function buildClearMemoryTombstones(memory, deletedAt = new Date().toISOString()) {
  let deletedNotes = (memory && memory.deletedNotes) || [];
  for (const e of (memory && memory.noteEntries) || []) {
    if (e && e.text) deletedNotes = addTombstone(deletedNotes, e.text, deletedAt);
  }
  let deletedFacts = (memory && memory.deletedFacts) || [];
  for (const r of (memory && memory.races) || []) deletedFacts = addFactTombstone(deletedFacts, 'race', raceKey(r), deletedAt);
  for (const r of (memory && memory.pastRaces) || []) deletedFacts = addFactTombstone(deletedFacts, 'race', raceKey(r), deletedAt);
  for (const i of (memory && memory.injuries) || []) deletedFacts = addFactTombstone(deletedFacts, 'injury', injuryKey(i), deletedAt);
  return { deletedNotes, deletedFacts };
}

// Deterministically collapse near-duplicate note entries. Groups by strong
// dedupeKey first (punctuation/dash/whitespace variants), then merges groups
// whose representative texts are the same concept (high token overlap). Within a
// group: keep the longest text as canonical, the earliest createdAt, the latest
// lastConfirmedAt, and durable kind if ANY member is durable. Order-stable.
export function dedupeNoteEntries(entries = []) {
  const groups = [];
  for (const e of entries) {
    if (!e || !e.text) continue;
    let g = groups.find(grp => dedupeKey(grp.rep) === dedupeKey(e.text) || sameConcept(grp.rep, e.text));
    if (!g) {
      g = { rep: e.text, members: [] };
      groups.push(g);
    }
    g.members.push(e);
    // Prefer the longest text in the group as the representative/canonical.
    if (e.text.length > g.rep.length) g.rep = e.text;
  }
  return groups.map(g => {
    const members = g.members;
    const canonical = members.reduce((a, b) => (b.text.length > a.text.length ? b : a));
    const earliest = members.reduce((a, b) =>
      (new Date(b.createdAt).getTime() || Infinity) < (new Date(a.createdAt).getTime() || Infinity) ? b : a);
    const latestConfirm = members
      .map(m => m.lastConfirmedAt)
      .filter(Boolean)
      .sort((x, y) => new Date(y) - new Date(x))[0] || null;
    const anyDurable = members.some(m => m.kind === 'durable');
    return {
      ...canonical,
      id: earliest.id || canonical.id,
      kind: anyDurable ? 'durable' : 'transient',
      createdAt: earliest.createdAt,
      lastConfirmedAt: latestConfirm,
    };
  });
}

// Drop (and tombstone) time-bound notes whose window has closed, regardless of
// kind. Returns a new memory when anything changed, else the same reference. The
// tombstone ensures a stale device can't resurrect the expired note.
export function expireDatedNotes(memory, now = Date.now()) {
  const entries = memory?.noteEntries || [];
  if (!entries.length) return memory;
  const expired = [];
  const kept = entries.filter(e => {
    if (isExpiredDatedNote(e.text, now)) { expired.push(e); return false; }
    return true;
  });
  if (!expired.length) return memory;
  const nowIso = new Date(now).toISOString();
  const deletedNotes = mergeTombstones(
    memory.deletedNotes || [],
    expired.map(e => makeTombstone(e.text, nowIso)),
    now
  );
  return { ...memory, noteEntries: kept, deletedNotes };
}

// The single client-side cleanup pipeline: migrate → expire aged transients →
// expire closed time-bound notes → apply tombstones → dedupe near-duplicates →
// GC tombstones. Idempotent; does NOT touch `lastUpdated` (callers decide
// whether the result differs enough to persist via memoryEquivalent).
//
// ORDER MATTERS: tombstones are applied to the RAW entries BEFORE semantic
// dedupe, exactly as the server does (mergeMemoryServer suppresses on the
// un-deduped union). Deduping first would collapse an old note and a genuinely
// re-learned post-deletion variant into one entry stamped with the EARLIEST
// createdAt, and applyTombstones would then wrongly delete the re-learned fact —
// while the server (no semantic dedupe) keeps it, causing client/server
// divergence. Per-entry suppression preserves the newest survivor's identity.
export function reconcileMemory(raw, now = Date.now()) {
  let memory = migrateMemory(raw);
  if (!memory || typeof memory !== 'object') return memory;
  memory = expireTransientNotes(memory, now);
  memory = expireDatedNotes(memory, now);
  const deletedNotes = gcTombstones(memory.deletedNotes || [], now);
  const suppressed = applyTombstones(memory.noteEntries || [], deletedNotes);
  const noteEntries = dedupeNoteEntries(suppressed);
  // Structured-fact tombstones: GC then suppress deleted races / injuries on
  // every load so a deletion survives storage round-trips and stale imports.
  const deletedFacts = gcFactTombstones(memory.deletedFacts || [], now);
  const withFactTombs = applyFactTombstones({ ...memory, noteEntries, deletedNotes, deletedFacts }, deletedFacts);
  return withFactTombs;
}

// Apply an AI compaction result to memory. Carries metadata forward for
// unchanged notes, mints fresh entries for genuinely new summaries, and
// tombstones every original that compaction dropped (so union merges and stale
// devices cannot resurrect it). This is what makes "Compact now" actually stick.
export function applyCompaction(memory, compacted, now = new Date()) {
  const nowIso = now.toISOString();
  const originals = memory?.noteEntries || [];
  const clean = (compacted || [])
    .filter(c => c && typeof c.text === 'string' && c.text.trim())
    .map(c => ({ text: c.text.trim(), kind: c.kind === 'durable' ? 'durable' : 'transient' }));
  const origByKey = new Map(originals.map(e => [noteKey(e.text), e]));
  const fresh = clean.map(c => {
    const prior = origByKey.get(noteKey(c.text));
    if (prior) return { ...prior, kind: c.kind };
    return {
      id: newId(),
      text: c.text,
      kind: c.kind,
      createdAt: nowIso,
      lastConfirmedAt: null,
      deferredUntil: null,
    };
  });
  const survives = (text) => fresh.some(f => sameConcept(f.text, text));
  const tombs = [];
  for (const e of originals) {
    if (survives(e.text)) continue;
    tombs.push(makeTombstone(e.text, nowIso));
  }
  const deletedNotes = mergeTombstones(memory?.deletedNotes || [], tombs, now.getTime());
  return { ...memory, noteEntries: fresh, deletedNotes, lastUpdated: nowIso };
}

export function shouldCompact(memory) {
  const entries = memory?.noteEntries || [];
  if (entries.length > COMPACT_TRIGGER_ENTRIES) return true;
  const chars = entries.reduce((sum, e) => sum + (e.text?.length || 0), 0);
  return chars > COMPACT_TRIGGER_CHARS;
}

// Pick a single transient entry to check in on this session.
// Criteria: kind=transient, age 7–21 days since createdAt or lastConfirmedAt,
// not deferred past `now`.
export function findStaleTransientCandidate(memory, now = Date.now()) {
  const entries = memory?.noteEntries || [];
  const minAge = now - CHECKIN_MIN_AGE_DAYS * DAY_MS;
  const maxAge = now - TRANSIENT_EXPIRY_DAYS * DAY_MS;
  const candidates = entries.filter(e => {
    if (e.kind !== 'transient') return false;
    if (e.deferredUntil && new Date(e.deferredUntil).getTime() > now) return false;
    // Never run a "still true?" check-in on a note that describes a specific past
    // dated event — the question is nonsensical for a historical fact. These are
    // dropped by expireTransientNotes instead.
    if (describesPastDatedEvent(e.text, now)) return false;
    const ref = new Date(e.lastConfirmedAt || e.createdAt).getTime();
    if (!Number.isFinite(ref)) return false;
    return ref < minAge && ref > maxAge;
  });
  if (!candidates.length) return null;
  // Prefer the oldest (closest to expiry).
  candidates.sort((a, b) => {
    const ra = new Date(a.lastConfirmedAt || a.createdAt).getTime();
    const rb = new Date(b.lastConfirmedAt || b.createdAt).getTime();
    return ra - rb;
  });
  return candidates[0];
}

// Format note entries for inclusion in AI prompts.
export function formatNoteEntriesForPrompt(entries = []) {
  if (!entries.length) return '';
  return entries
    .map(e => `[${e.kind}] ${e.text}`)
    .join('\n');
}
